/**
 * Runtime configuration (design SS10).
 *
 * Everything here is baked in at build time by Vite, which is fine because none
 * of it is secret: the Cognito client is public by design (design SS3.1), and a
 * client id in a bundle is not a credential.
 */

export interface AppConfig {
  apiBaseUrl: string
  cognitoDomain: string
  cognitoClientId: string
  redirectUri: string
  /**
   * Dev-only impersonation. When set, the API client sends the `x-test-user-*`
   * headers the local dev server accepts instead of a bearer token, so the whole
   * app is usable against DynamoDB Local without a Cognito pool.
   *
   * Guarded by `import.meta.env.DEV` at the point of use, so a production bundle
   * cannot honour it even if the variable is present in the build environment.
   */
  devUser?: { userId: string; email: string; displayName: string }
}

function readDevUser(): AppConfig['devUser'] {
  if (!import.meta.env.DEV) return undefined
  const userId = import.meta.env.VITE_DEV_USER_ID
  if (!userId) return undefined
  return {
    userId,
    email: import.meta.env.VITE_DEV_USER_EMAIL ?? `${userId}@example.test`,
    displayName: import.meta.env.VITE_DEV_USER_NAME ?? 'Dev User',
  }
}

export const config: AppConfig = {
  // The dev server proxies /api to the local API (see vite.config.ts), so the
  // browser stays same-origin and the production CSP needs no dev exception.
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '/api',
  cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN ?? '',
  cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
  redirectUri: import.meta.env.VITE_REDIRECT_URI ?? `${window.location.origin}/login`,
  devUser: readDevUser(),
}

/** Whether a real hosted-UI login is possible, as opposed to dev impersonation. */
export function hasCognito(): boolean {
  return config.cognitoDomain !== '' && config.cognitoClientId !== ''
}
